import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '@soap/modules';
import { Camera, Upload, X } from 'lucide-react';
import { Button } from '../../ui/button';
import { uploadBlobFile } from '../../utils/uploadBlob';
import type { ManualLessonEntry, UnitContentSource } from './createUnitTypes';
import { unitContentSources } from './createUnitTypes';
import { ExternalResourceProviderSelect } from './ExternalResourceProviderSelect';

interface ExternalLessonInputSectionProps {
  contentSource: UnitContentSource;
  providerId: string;
  onProviderChange: (id: string) => void;
  providerError?: string;
  sourceBlobIds: string[];
  onSourceBlobIdsChange: (ids: string[]) => void;
  onBusyChange?: (busy: boolean) => void;
  onPhotoCaptured?: (blobIds: string[]) => void;
}

function parseManualEntries(text: string): ManualLessonEntry[] {
  return text
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .map(line => {
      const pipe = line.indexOf('|');
      if (pipe > 0) {
        return {
          title: line.slice(0, pipe).trim(),
          summary: line.slice(pipe + 1).trim()
        };
      }
      return { title: line, summary: '' };
    });
}

export function parseManualLessonText(text: string): ManualLessonEntry[] {
  return parseManualEntries(text);
}

export function formatManualLessonText(entries: ManualLessonEntry[]): string {
  return entries
    .filter(entry => entry.title.trim())
    .map(entry => {
      const title = entry.title.trim();
      const summary = (entry.summary ?? '').trim();
      return summary ? `${title} | ${summary}` : title;
    })
    .join('\n');
}

export function ExternalLessonInputSection({
  contentSource,
  providerId,
  onProviderChange,
  providerError,
  sourceBlobIds,
  onSourceBlobIdsChange,
  onBusyChange,
  onPhotoCaptured
}: ExternalLessonInputSectionProps) {
  const { idToken, authReady } = useAuth('external-lesson-input');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [uploading, setUploading] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  const [previewReady, setPreviewReady] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  const isOnline = contentSource === unitContentSources.externalOnline;

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setPreviewReady(false);
    setCameraOn(false);
  };

  useEffect(() => {
    return () => {
      streamRef.current?.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    };
  }, []);

  useEffect(() => {
    onBusyChange?.(cameraOn || uploading);
  }, [cameraOn, uploading, onBusyChange]);

  useEffect(() => {
    if (!cameraOn) return;
    const video = videoRef.current;
    const stream = streamRef.current;
    if (!video || !stream) return;
    video.srcObject = stream;
    const markReady = () => setPreviewReady(true);
    video.addEventListener('loadedmetadata', markReady);
    void video.play().catch(() => {
      setUploadError('Could not start the camera preview.');
      stopCamera();
    });
    return () => {
      video.removeEventListener('loadedmetadata', markReady);
    };
  }, [cameraOn]);

  const startCamera = async () => {
    setUploadError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } }
      });
      streamRef.current = stream;
      setCameraOn(true);
    } catch {
      setUploadError('Could not access the camera.');
    }
  };

  const capturePhoto = async () => {
    if (!authReady || !idToken) {
      setUploadError('Sign in again, then capture the photo.');
      return;
    }
    if (!videoRef.current) return;
    const video = videoRef.current;
    if (!video.videoWidth || !video.videoHeight) {
      setUploadError('Wait for the camera preview before capturing.');
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    setUploading(true);
    setUploadError(null);
    try {
      const blob = await new Promise<Blob | null>(resolve =>
        canvas.toBlob(resolve, 'image/jpeg', 0.85)
      );
      if (!blob) throw new Error('Capture failed');
      const previewUrl = URL.createObjectURL(blob);
      const file = new File([blob], `lesson-page-${Date.now()}.jpg`, { type: 'image/jpeg' });
      const meta = await uploadBlobFile(file, idToken);
      const nextIds = [...sourceBlobIds, meta.id];
      onSourceBlobIdsChange(nextIds);
      setPhotoPreviews(current => [...current, previewUrl]);
      stopCamera();
      onPhotoCaptured?.(nextIds);
    } catch {
      setUploadError('Failed to upload photo.');
    } finally {
      setUploading(false);
    }
  };

  const handleFile = async (file: File | null) => {
    if (!file || !authReady || !idToken) return;
    setUploading(true);
    setUploadError(null);
    try {
      const meta = await uploadBlobFile(file, idToken);
      onSourceBlobIdsChange([...sourceBlobIds, meta.id]);
    } catch {
      setUploadError('Failed to upload file.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <ExternalResourceProviderSelect
        contentSource={contentSource}
        value={providerId}
        onChange={onProviderChange}
        error={providerError}
      />

      {isOnline ? (
        <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
          <p className="font-medium mb-1">Browser security limitation</p>
          <p>
            We cannot read other websites directly from your browser. Print the page as a PDF and upload it
            here so we can incorporate the lessons.
          </p>
        </div>
      ) : null}

      <div className="mb-5">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {isOnline ? 'Upload PDF' : 'Photo of lesson page'}
        </h3>
        <p className="text-sm text-gray-600 mb-3">
          {isOnline
            ? 'Upload a PDF export of the online lesson content. Next we will read it and show the lessons we plan to create so you can edit them. You can also continue without a file and type lesson names on the next step.'
            : 'Take a photo of the book page. Next we will read it and show the lessons we plan to create so you can edit them. You can also continue without a photo and type lesson names on the next step.'}
        </p>

        {!isOnline && (
          <div className="mb-3">
            {!cameraOn ? (
              <Button type="button" variant="outline" size="sm" onClick={startCamera}>
                <Camera className="w-4 h-4 mr-1" />
                Use camera
              </Button>
            ) : (
              <div className="space-y-2">
                <video
                  ref={videoRef}
                  className="aspect-video w-full rounded border border-gray-200 bg-black object-cover"
                  playsInline
                  muted
                  autoPlay
                />
                <div className="flex gap-2">
                  <Button type="button" size="sm" onClick={capturePhoto} disabled={uploading || !previewReady}>
                    {previewReady ? 'Capture' : 'Starting camera...'}
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={stopCamera}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {isOnline ? (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              className="hidden"
              onChange={e => {
                void handleFile(e.target.files?.[0] ?? null);
                e.target.value = '';
              }}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="w-4 h-4 mr-1" />
              {uploading ? 'Uploading...' : 'Upload PDF'}
            </Button>
          </>
        ) : null}

        {sourceBlobIds.length > 0 && (
          <ul className="mt-2 space-y-2">
            {sourceBlobIds.map((id, index) => (
              <li key={id} className="flex items-center gap-2 text-sm text-gray-700 bg-gray-50 rounded px-2 py-1">
                {photoPreviews[index] ? (
                  <img src={photoPreviews[index]} alt="" className="h-12 w-16 rounded object-cover border border-gray-200" />
                ) : null}
                <span className="flex-1">{isOnline ? `Uploaded file ${index + 1}` : `Photo ${index + 1}`}</span>
                <button
                  type="button"
                  className="text-gray-400 hover:text-destructive-action"
                  onClick={() => {
                    const preview = photoPreviews[index];
                    if (preview) URL.revokeObjectURL(preview);
                    setPhotoPreviews(current => current.filter((_, i) => i !== index));
                    onSourceBlobIdsChange(sourceBlobIds.filter((_, i) => i !== index));
                  }}
                >
                  <X className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}

        {uploadError ? <p className="text-sm text-destructive-action mt-2">{uploadError}</p> : null}
      </div>
    </>
  );
}
